<script>
  import { createEventDispatcher, onMount } from 'svelte'
  import { Map, Polyline, Marker } from '../../components/Map'

  export let waypoints = []
  export let activeWaypoint = null
  export let viewport = null
  export let maxZoom = 15
  export let minZoom = 4
  export let naturalZoom = 12

  const dispatch = createEventDispatcher()

  let polylines = []
  let centre = [0, 0]
  let zoom = minZoom
  let tripBoundsZoom = -1
  let map
  $: visibleWaypoints = waypoints.filter(wp => wp.isStop() || wp.hasContent() )

  // Add polyline between each stop, routed via reading positions
  $: {
    let currentPolyline = []
    for (let wp of waypoints) {
      if (wp.isStop()) {
        if (currentPolyline.length > 0) currentPolyline.push(wp.latlng)
        polylines.push(currentPolyline)
        currentPolyline = [wp.latlng]
      } else if (wp.isReading()) {
        currentPolyline.push(wp.latlng)
      }
    }
    if (visibleWaypoints.length > 0) centre = visibleWaypoints[0].latlng
  }

  $: fitBoundsPadding = viewport
    ? {
      paddingTopLeft: viewport.topLeft || [0, 0],
      paddingBottomRight: viewport.bottomRight || [0, 0],
    } : {}

  onMount(() => {
    // On load, fit to bounds to avoid flyToBounds starting from minZoom
		map.fitBounds(visibleWaypoints.map(wp => wp.latlng), fitBoundsPadding)
		tripBoundsZoom = map.getZoom()
	})

  $: if (map && !activeWaypoint) {
    // If zooming out to show whole trip, use slow flyToBounds
    map.flyToBounds(visibleWaypoints.map(wp => wp.latlng), { maxZoom: naturalZoom, animate: true, duration: 1, ...fitBoundsPadding })
  }

  $: if (map && activeWaypoint) {
    const bounds = [
      activeWaypoint.latlng,
      ...activeWaypoint.entries.map(entry => entry.latlng).filter(x => !!x),
    ]
    // Use fitBounds for moving between waypoints; flyToBounds causes rendering issues with polylines and markers.
    map.fitBounds(bounds, {
      animate: true, duration: 0.5,
      maxZoom: map.getZoom() <= tripBoundsZoom ? naturalZoom : map.getZoom(), // TODO: only auto-zoom if user has never zoomed
      ...fitBoundsPadding
    })
  }

  const sizeForWaypoint = (currentZoom, waypoint) => {
    // Any Waypoint with images is always largest for zoom level
    // Stop with no mages is one below that
    // Reading with entries is one below that
    // Reading with content but no entries is always xs

    const zoomDelta = naturalZoom - currentZoom
    if (zoomDelta > 6) {
      return 'xs'
    } else if (zoomDelta > 4) {
      if (waypoint.hasImages()) return 'md'
      if (waypoint.isReading()) return 'xs'
      return 'sm'
    } else if (zoomDelta > 1) {
      if (waypoint.hasImages()) return 'lg'
      if (waypoint.isReading() && waypoint.hasEntries()) return 'sm'
      if (waypoint.isReading() && waypoint.hasContent()) return 'xs'
      return 'sm'
    } else {
      if (waypoint.hasImages()) return 'xl'
      if (waypoint.isReading() && waypoint.hasEntries()) return 'sm'
      if (waypoint.isReading() && waypoint.hasContent()) return 'xs'
      return 'lg'
    }
  }
</script>

<Map bind:map {centre} zoom={minZoom} maxZoom={maxZoom} on:zoom={({ detail: newZoom }) => {zoom = newZoom}}>
    {#each visibleWaypoints as wp}
        <Marker
            lat={wp.lat}
            lng={wp.lng}
            image={wp.hasImages() ? wp.images[0] : null}
            size={sizeForWaypoint(zoom, wp)}
            active={wp === activeWaypoint}
            on:click={() => dispatch('waypoint-marker-click', wp)}
        />
    {/each}

    {#if activeWaypoint && zoom > 7}
        {#each activeWaypoint.entries as entry}
            {#if !entry.atPosition(activeWaypoint.latlng)}
                <Marker
                    lat={entry.lat}
                    lng={entry.lng}
                    image={entry.coverImage}
                    size='md'
                    active
                    on:click={() => dispatch('entry-marker-click', entry)}
                />
                <Polyline
                    latlngs={[activeWaypoint.latlng, entry.latlng]}
                    lineStyle='dashed'
                    active
                />
            {/if}
        {/each}
    {/if}

    {#each polylines as polyline}
        <Polyline
            latlngs={polyline}
            lineStyle='solid'
        />
    {/each}
</Map>
